import PropTypes from 'prop-types';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, Button, IconButton, Avatar } from '@mui/material';
import { DarkMode, LightMode } from '@mui/icons-material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useSelector } from 'react-redux';
import Logo from '../../components/Logo';
import SearchBar from '../../components/SearchBar';
import NotificationBell from '../../components/NotificationBell';
import MessagesButton from '../../components/MessagesButton';
import StreakPill from '../../components/StreakPill';
import AccountPopover from './AccountPopover';
import { useDarkModeContext } from '../../context/DarkModeContext';
import { getGuestAvatar } from '../../utils/deviceId';

// Top-level pages that users "live on" - persistent tabs or always-available
// home surfaces. These show the logo, no back button. Every OTHER dashboard
// path is treated as a sub-page and gets a back button on mobile.
//
// Note: /new (compose) is intentionally NOT a root even though it's in the
// bottom nav - users open it to upload, then either finish or abandon. A
// back button makes that exit path discoverable.
const BOTTOM_NAV_ROOTS = [
  '/',
  '/discover',
  '/play',
  '/u/me',
];
const isBottomNavRoot = (pathname) =>
  BOTTOM_NAV_ROOTS.includes(pathname) || BOTTOM_NAV_ROOTS.includes(pathname.replace(/\/$/, ''));

const DRAWER_WIDTH = 240;
const COLLAPSED_DRAWER_WIDTH = 72;
const APPBAR_MOBILE = 56;
const APPBAR_DESKTOP = 56;

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'transparent' && prop !== 'visible',
})(({ theme, transparent, visible }) => ({
  boxShadow: 'none',
  backgroundColor: transparent ? 'transparent' : theme.palette.background.default,
  backgroundImage: 'none',
  borderBottom: transparent ? 'none' : `1px solid ${theme.palette.divider}`,
  paddingTop: 'env(safe-area-inset-top)',
  transition: theme.transitions.create(['width', 'margin', 'transform'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  transform: visible ? 'translateY(0)' : 'translateY(-100%)',
  [theme.breakpoints.down('lg')]: {
    width: '100%',
    transform: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    ...(transparent && { display: 'none' }),
  },
  [theme.breakpoints.up('lg')]: {
    width: `calc(100% - ${DRAWER_WIDTH}px)`,
  },
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  // Match the body content's horizontal padding (DashboardApp uses
  // px: { xs: 0.5, sm: 1, md: 1.5 }) so the navbar items align to the
  // same vertical edges as the page below. MUI Toolbar's default 16px
  // horizontal padding on mobile leaves the nav inset further than the
  // body, which is what the user noticed.
  paddingLeft: `calc(${theme.spacing(0.5)} + env(safe-area-inset-left))`,
  paddingRight: `calc(${theme.spacing(0.5)} + env(safe-area-inset-right))`,
  [theme.breakpoints.up('sm')]: {
    paddingLeft: `calc(${theme.spacing(1)} + env(safe-area-inset-left))`,
    paddingRight: `calc(${theme.spacing(1)} + env(safe-area-inset-right))`,
  },
  [theme.breakpoints.up('md')]: {
    paddingLeft: `calc(${theme.spacing(1.5)} + env(safe-area-inset-left))`,
    paddingRight: `calc(${theme.spacing(1.5)} + env(safe-area-inset-right))`,
  },
  [theme.breakpoints.up('lg')]: {
    minHeight: APPBAR_DESKTOP,
    padding: theme.spacing(0, 2),
  },
}));

DashboardNavbar.propTypes = {
  categoryTabs: PropTypes.node,
  transparent: PropTypes.bool,
  visible: PropTypes.bool,
};

export default function DashboardNavbar({ categoryTabs, transparent, visible = true }) {
  const { userInfo } = useSelector((state) => state.userLogin);
  const { mode, toggleMode } = useDarkModeContext();
  const location = useLocation();
  const navigate = useNavigate();
  const showBack = !isBottomNavRoot(location.pathname);
  // Fall back to "/" when there's nothing in the history (e.g. a deep link
  // loaded as the first entry). Chrome's `history.length` starts at 1 for
  // the initial page; anything > 1 means we have somewhere to go back.
  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  const isVotePage = /^\/v\/[^/]+/.test(location.pathname);

  return (
    <RootStyle
      transparent={transparent}
      visible={visible}
      sx={(theme) => ({
        ...(isVotePage ? {
          [theme.breakpoints.up('lg')]: {
            backgroundColor: 'transparent',
            borderBottom: 'none',
            pointerEvents: 'none',
            // Hidden entirely. The vote surface is a full-height overlay at
            // zIndex 1200 with its OWN header (back button + title), so a
            // second navbar either renders behind the column - search field
            // vanishing under its left edge - or, if floated into the right
            // gutter, overflows the window on a 1280 screen because that
            // gutter is narrower than the toolbar. Below `lg` the navbar is
            // already hidden for transparent pages, so this just extends the
            // same treatment upward.
            display: 'none',
          }
        } : {})
      })}
    >
      <ToolbarStyle>
        {/* Mobile: back button on sub-pages, logo on bottom-nav roots. */}
        <Box sx={{ display: { xs: 'flex', lg: 'none' }, alignItems: 'center' }}>
          {showBack ? (
            <IconButton
              aria-label="Back"
              onClick={handleBack}
              sx={{ color: 'text.primary', ml: -1 }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />
            </IconButton>
          ) : (
            <Logo />
          )}
        </Box>

        {/* Center: categories on desktop, empty space/spacer on mobile */}
        <Box 
          sx={(theme) => ({ 
            flex: 1, 
            display: { xs: 'none', md: 'flex' }, 
            justifyContent: 'center', 
            overflow: 'hidden',
            px: 2,
            ...(isVotePage ? {
              [theme.breakpoints.up('lg')]: {
                display: 'none',
              }
            } : {})
          })}
        >
          {categoryTabs}
        </Box>
        {/* Spacer on mobile */}
        <Box sx={{ flex: 1, display: { xs: 'flex', md: 'none' } }} />

        {/* Right: search, notifications, account (dark-mode toggle lives inside
            AccountPopover when logged in; surfaced here for logged-out users). */}
        <Stack
          direction="row"
          alignItems="center"
          sx={(theme) => ({
            gap: 1,
            flexShrink: 0,
            pointerEvents: 'auto',
            ...(isVotePage ? {
              [theme.breakpoints.up('lg')]: {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(28, 28, 36, 0.94)' : 'rgba(255, 255, 255, 0.95)',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '30px',
                px: 2,
                py: 0.75,
                mr: 2,
                mt: 1.5,
                boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.3)' : '0 4px 20px rgba(0, 0, 0, 0.05)',
              }
            } : {})
          })}
        >
          <SearchBar />
          {/* DMs beside the bell from `sm` up. Below `sm` the button hides
              itself (it measured as clipping the avatar at 375px — see
              MessagesButton.js); phones reach the inbox via the People hub,
              and the BottomNav People badge carries the unread signal there.
              Renders null for logged-out and anonymous users. */}
          <MessagesButton />
          <NotificationBell />
          <StreakPill />
          <IconButton
            onClick={toggleMode}
            size="small"
            aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            sx={{
              color: 'text.primary',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            {mode === 'dark' ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
          </IconButton>
          {userInfo ? (
            <AccountPopover />
          ) : (
            <>
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                color="primary"
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  px: 2,
                  py: 0.5,
                  minWidth: 'auto',
                  display: { xs: 'none', md: 'inline-flex' },
                }}
              >
                Log in
              </Button>
              <IconButton
                component={RouterLink}
                to="/login"
                aria-label="Log in"
                sx={{ p: 0, display: { xs: 'flex', md: 'none' } }}
              >
                <Avatar src={getGuestAvatar()} sx={{ width: 32, height: 32 }} />
              </IconButton>
            </>
          )}
        </Stack>
      </ToolbarStyle>
    </RootStyle>
  );
}
